import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ConfigStorageService, SavedConfigSummary, SavedConfig } from '../services/config-storage';
import { Trash2, Download, Upload, Save, Clock, FileText, Eye, Edit3, X } from 'lucide-react';

interface SavedConfigsManagerProps {
  sessionId: string;
  currentConfig: any;
  onLoadConfig: (config: any) => void;
}

export function SavedConfigsManager({ sessionId, currentConfig, onLoadConfig }: SavedConfigsManagerProps) {
  const [savedConfigs, setSavedConfigs] = useState<SavedConfigSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<SavedConfig | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [editingConfig, setEditingConfig] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  useEffect(() => {
    loadSavedConfigs();
  }, []);

  const loadSavedConfigs = async () => {
    setLoading(true);
    try {
      const configs = await ConfigStorageService.loadSavedConfigs();
      setSavedConfigs(configs);
    } catch (error) {
      console.error('Error loading saved configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    if (!saveName.trim()) return;

    setLoading(true);
    try {
      const result = await ConfigStorageService.saveConfig(saveName, currentConfig, sessionId);
      if (result.success) {
        setSaveName('');
        setShowSaveDialog(false);
        await loadSavedConfigs();
      }
    } catch (error) {
      console.error('Error saving config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadConfig = async (configId: string) => {
    setLoading(true);
    try {
      const config = await ConfigStorageService.loadConfig(configId);
      if (config) {
        onLoadConfig({
          triggers: config.triggers,
          categories: config.categories
        });
      }
    } catch (error) {
      console.error('Error loading config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;

    setLoading(true);
    try {
      const result = await ConfigStorageService.deleteConfig(configId);
      if (result.success) {
        await loadSavedConfigs();
      }
    } catch (error) {
      console.error('Error deleting config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async () => {
    setLoading(true);
    try {
      await ConfigStorageService.autoSaveCurrentConfig(currentConfig, sessionId);
      await loadSavedConfigs();
    } catch (error) {
      console.error('Error auto-saving config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewConfig = async (configId: string) => {
    setPreviewLoading(true);
    try {
      const config = await ConfigStorageService.loadConfig(configId);
      setSelectedConfig(config);
    } catch (error) {
      console.error('Error previewing config:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setSelectedConfig(null);
  };

  const handleStartEdit = (configId: string, currentName: string) => {
    setEditingConfig(configId);
    setEditName(currentName);
  };

  const handleCancelEdit = () => {
    setEditingConfig(null);
    setEditName('');
  };

  const handleSaveEdit = async () => {
    if (!editingConfig || !editName.trim()) return;

    setLoading(true);
    try {
      // Load the full config first
      const config = await ConfigStorageService.loadConfig(editingConfig);
      if (config) {
        // Save with new name
        const result = await ConfigStorageService.saveConfig(editName.trim(), {
          triggers: config.triggers,
          categories: config.categories
        }, config.sessionId || sessionId);
        
        if (result.success) {
          // Delete the old config
          await ConfigStorageService.deleteConfig(editingConfig);
          // Reload the list
          await loadSavedConfigs();
          handleCancelEdit();
        }
      }
    } catch (error) {
      console.error('Error renaming config:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Saved Configurations
          </h2>
          <div className="flex gap-2">
            <Button
              onClick={handleAutoSave}
              disabled={loading}
              variant="outline"
              size="sm"
            >
              <Save className="h-4 w-4 mr-2" />
              Auto-save
            </Button>
            <Button
              onClick={() => setShowSaveDialog(true)}
              disabled={loading}
              size="sm"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Current
            </Button>
          </div>
        </div>

        {showSaveDialog && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold mb-2">Save Configuration</h3>
            <div className="flex gap-2">
              <Input
                placeholder="Enter configuration name..."
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSaveConfig()}
              />
              <Button onClick={handleSaveConfig} disabled={loading || !saveName.trim()}>
                Save
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowSaveDialog(false);
                  setSaveName('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
            <p className="mt-2 text-sm text-gray-600">Loading...</p>
          </div>
        )}

        {!loading && savedConfigs.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No saved configurations found.</p>
            <p className="text-sm">Save your current configuration to get started.</p>
          </div>
        )}

        {!loading && savedConfigs.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {savedConfigs.map((config) => (
              <Card key={config.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  {editingConfig === config.id ? (
                    <div className="flex-1 mr-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveEdit()}
                        className="text-sm font-semibold"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <h3 className="font-semibold text-sm truncate flex-1 mr-2">
                      {config.name}
                    </h3>
                  )}
                  <div className="flex gap-1">
                    {editingConfig === config.id ? (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleSaveEdit}
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700"
                          title="Save changes"
                          disabled={!editName.trim()}
                        >
                          <Save className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEdit}
                          className="h-8 w-8 p-0 text-gray-600 hover:text-gray-700"
                          title="Cancel editing"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleStartEdit(config.id, config.name)}
                          className="h-8 w-8 p-0"
                          title="Rename configuration"
                        >
                          <Edit3 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handlePreviewConfig(config.id)}
                          className="h-8 w-8 p-0"
                          title="Preview configuration"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleLoadConfig(config.id)}
                          className="h-8 w-8 p-0"
                          title="Load configuration"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteConfig(config.id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                          title="Delete configuration"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="text-xs text-gray-500 space-y-1">
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>Created: {formatDate(config.createdAt)}</span>
                  </div>
                  {config.updatedAt !== config.createdAt && (
                    <div className="flex items-center gap-1">
                      <Upload className="h-3 w-3" />
                      <span>Updated: {formatDate(config.updatedAt)}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-6 text-xs text-gray-500">
          <p><strong>Auto-save:</strong> Automatically saves your current configuration with timestamp</p>
          <p><strong>Save Current:</strong> Manually save with a custom name</p>
          <p><strong>Rename:</strong> Edit the name of any saved configuration (useful for auto-saves)</p>
          <p><strong>Preview:</strong> View the content of a configuration before loading</p>
          <p><strong>Load:</strong> Apply a saved configuration to your current session</p>
        </div>
      </Card>

      {/* Configuration Preview Modal */}
      {selectedConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Configuration Preview: {selectedConfig.name}</h3>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClosePreview}
                >
                  Close
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-[70vh] overflow-y-auto">
                {/* Triggers Section */}
                <div>
                  <h4 className="font-semibold mb-3 text-blue-600">🎯 Triggers</h4>
                  <div className="space-y-3">
                    {Object.entries(selectedConfig.triggers || {}).map(([lang, triggers]) => (
                      <div key={lang} className="bg-blue-50 p-3 rounded">
                        <div className="font-medium text-sm mb-2 uppercase">{lang}</div>
                        <div className="space-y-1">
                          {(triggers as string[]).map((trigger, index) => (
                            <div key={index} className="text-sm bg-white p-2 rounded border">
                              {trigger}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Categories Section */}
                <div>
                  <h4 className="font-semibold mb-3 text-green-600">📂 Categories</h4>
                  <div className="space-y-3">
                    {Object.entries(selectedConfig.categories || {}).map(([lang, categories]) => (
                      <div key={lang} className="bg-green-50 p-3 rounded">
                        <div className="font-medium text-sm mb-2 uppercase">{lang}</div>
                        <div className="space-y-1">
                          {(categories as string[]).map((category, index) => (
                            <div key={index} className="text-sm bg-white p-2 rounded border">
                              {category}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="mt-6 pt-4 border-t">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                  <div>
                    <strong>Created:</strong><br />
                    {formatDate(selectedConfig.createdAt)}
                  </div>
                  <div>
                    <strong>Session ID:</strong><br />
                    {selectedConfig.sessionId || 'N/A'}
                  </div>
                  <div>
                    <strong>Total Triggers:</strong><br />
                    {Object.values(selectedConfig.triggers || {}).reduce((total, triggers) => 
                      total + (triggers as string[]).length, 0
                    )}
                  </div>
                  <div>
                    <strong>Total Categories:</strong><br />
                    {Object.values(selectedConfig.categories || {}).reduce((total, categories) => 
                      total + (categories as string[]).length, 0
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={handleClosePreview}
                >
                  Close Preview
                </Button>
                <Button
                  onClick={() => {
                    onLoadConfig({
                      triggers: selectedConfig.triggers,
                      categories: selectedConfig.categories
                    });
                    handleClosePreview();
                  }}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Load This Configuration
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Preview Loading Overlay */}
      {previewLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="p-6">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-4"></div>
              <p>Loading configuration preview...</p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
